import React from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useEffect } from 'react';
import { useState } from 'react';
import ComponentHeaderTitles from '../ComponentHeaderTitles';
import { FormHelperText } from '@mui/material';
import TerugleveringInput from '../HoeveelStroomTerug';
import VerbruikInschatter from '../VerbruikInschatter';
import { shouldShowZelfopwek } from '../../config/generalConfig';

/**
 * Renders a component for inputting the user's energy usage.
 * If the user has already estimated their energy usage, the values will be pre-filled.
 * If the user manually inputs their energy usage, the values will be stored in the finalChosenUsage state, which
 * will be used to pre-fill the values if they navigate back to this step.
 * @param {Object} props - The component props.
 * @param {Array} props.chosenProduct - An array of chosen products (e.g. "Electriciteit", "Gas").
 * @param {Object} props.verbruikEstimaterStep2Output - The output of the VerbruikEstimaterStep2 component.
 * @param {Object} props.finalChosenUsage - The final chosen energy usage.
 * @param {Function} props.setFinalChosenUsage - A function to set the final chosen energy usage.
 * @returns {JSX.Element} - The rendered component.
 */
const VerbruikInput = ({
    chosenProduct,
    dubbeleMeter,
    setDubbeleMeter,
    returnsEnergy,
    setReturnsEnergy,
    returnedEnergyAmount,
    setReturnedEnergyAmount,
    verbruikEstimaterStep2Output,
    setVerbruikEstimaterStep2Output,
    finalChosenUsage,
    setFinalChosenUsage,
    onUpdateValidationErrors,
    selectedBuildingType,
    setSelectedBuildingType,
    householdSize,
    sethouseholdSize,
    solarPanels,
    setSolarPanels,
    entiteit,
    setIsFetchingData,
    returnsAvailableForAgent,
    agentID


}) => {
    const [estimatedVerbruik, setEstimatedVerbruik] = React.useState(() => {
        if (finalChosenUsage) {
            return finalChosenUsage;
        } else if (verbruikEstimaterStep2Output) {
            return verbruikEstimaterStep2Output;
        }
        return {}; // Default to an empty object if no initial value is provided
    });

    const [gasInputError, setGasInputError] = React.useState(false);
    const [eHighInputError, setEHighInputError] = React.useState(false);
    const [eLowInputError, setELowInputError] = React.useState(false);
    const [eSingleInputError, setESingleInputError] = React.useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => setModalOpen(false);

    const handleOnBlur = (field, value) => {
        // check if input is empty, null or undefined
        if (value === '' || value === null || value === undefined) {
            // set the error state to true
            if (field === 'gas') {
                setGasInputError(true);
            } else if (field === 'e_high') {
                setEHighInputError(true);
            } else if (field === 'e_low') {
                setELowInputError(true);
            } else if (field == "e_single") {
                setESingleInputError(true);
            }
        } else {
            // set the error state to false
            if (field === 'gas') {
                setGasInputError(false);
            } else if (field === 'e_high') {
                setEHighInputError(false);
            } else if (field === 'e_low') {
                setELowInputError(false);
            } else if (field == 'e_single') {
                setESingleInputError(false);
            }
        }
    }

    useEffect(() => {
        if (dubbeleMeter) {
            if (!eSingleInputError && !gasInputError) {
                onUpdateValidationErrors([]);
            } else {
                gasInputError && onUpdateValidationErrors(['Gelieve je gasverbruik in te vullen. Verbuis je geen gas? Vul dan 0 in.', false]);
                eSingleInputError && onUpdateValidationErrors(['Gelieve je stroomverbruik in te vullen. Verbruik je gep geen stroom? Vul dan 0 in.', false]);
            }
        } else {
            if (!gasInputError && !eHighInputError && !eLowInputError) {
                onUpdateValidationErrors([]);
            } else {
                gasInputError && onUpdateValidationErrors(['Gelieve je gasverbruik in te vullen. Verbuis je geen gas? Vul dan 0 in.', false]);
                eHighInputError && onUpdateValidationErrors(['Gelieve je hoog stroomverbruik in te vullen. Verbruik je geen hoog stroomverbruik', false]);
                eLowInputError && onUpdateValidationErrors(['Gelieve je laag stroomverbruik in te vullen. Verbruik je geen laag stroomverbruik', false]);

            };
        }

    }, [gasInputError, eHighInputError, eLowInputError, eSingleInputError, dubbeleMeter]);


    const handleChange = (field, value) => {
        const newValue = value.trim();
        const re = /^[0-9\b]+$/;
        // If value is empty or passes the regex test, update the state
        if (newValue === "" || re.test(newValue)) {
            setEstimatedVerbruik(prevValues => {
                // Create a new object with the updated field value
                const updatedValues = { ...prevValues, [field]: newValue };

                // Update the finalChosenUsage in the parent component
                // This function should correctly merge the new field value with the existing state
                setFinalChosenUsage(prevFinalChosenUsage => ({
                    ...prevFinalChosenUsage,
                    [field]: newValue
                }));

                return updatedValues;
            });
        }
    };


    /**
     * On mount, check if the finalChosenUsage is already set. If not, set it to the verbruikEstimaterStep2Output (if it exists)
     * This ensures that if the user accepts the estimated values, the estimated values will be used to set the finalChosenUsage.
     * 
     * Also has some logic regarding dubbeleMeter to remove/add properties to finalChosenUsage
     */
    React.useEffect(() => {

        var keysThatShouldExist = []
        if (chosenProduct.includes('Gas')) {
            keysThatShouldExist.push('gas')
        }
        if (chosenProduct.includes('Electriciteit')) {
            if (dubbeleMeter) {
                keysThatShouldExist.push('e_high', 'e_low');
            } else {
                keysThatShouldExist.push('e_single');
            }
        } else {
            // no electricity, so set teruglevering to false (just in case)
            setReturnsEnergy(false);
        }


        if (verbruikEstimaterStep2Output) {
            // check if all keys of finalChosenUsage are empty
            // filter out properties that are not e_high, e_low, e_single or gas
            const filteredOutput = Object.keys(verbruikEstimaterStep2Output)
                .filter(key => keysThatShouldExist.includes(key))
                .reduce((obj, key) => {
                    obj[key] = finalChosenUsage[key] ? finalChosenUsage[key] : verbruikEstimaterStep2Output[key];
                    return obj;
                }, {});

            setEstimatedVerbruik(filteredOutput); // immediately update the local state
            setFinalChosenUsage(filteredOutput); // update the finalChosenUsage in the parent component
        } else if ((!finalChosenUsage || Object.values(finalChosenUsage).some(value => value === null || value === undefined)) && !verbruikEstimaterStep2Output) {
            if (estimatedVerbruik) {
                // Make local copy so that we don't mutate the original state
                let estimatedVerbruikForCopy = { ...estimatedVerbruik };

                // check that estimatedVerbruikForCopy has the required keys. If not, add required keys (set to null)
                keysThatShouldExist.forEach(key => {
                    if (!estimatedVerbruikForCopy.hasOwnProperty(key)) {
                        estimatedVerbruikForCopy[key] = null;
                    }
                });

                setFinalChosenUsage(estimatedVerbruikForCopy);

            } else {
                const initialValues = keysThatShouldExist.reduce((obj, key) => {
                    obj[key] = null;
                    return obj;
                }, {});
                setFinalChosenUsage(initialValues);


            }
        }
    }, [dubbeleMeter, verbruikEstimaterStep2Output]); // The empty dependency array ensures this effect only runs once on mount



    return (
        <div>
            <ComponentHeaderTitles
                title="Wat is je verbruik?"
            />
            {chosenProduct.includes('Electriciteit') && (
            <FormGroup>
                <FormControlLabel
                    control={
                        <Switch
                            checked={dubbeleMeter}
                            onChange={() => setDubbeleMeter(!dubbeleMeter)}
                            name="dubbeleMeterSwitch"
                            color="primary"
                        />
                    }
                    label={dubbeleMeter ? 'Dubbele Meter' : 'Dubbele Meter'} // Toont of de switch aan of uit staat
                />
            </FormGroup>
            )}
            {chosenProduct.map((product, index) => (
                <div key={index}>
                    {product === "Electriciteit" ? (
                        <>
                            {!dubbeleMeter ? (
                                <FormControl variant="outlined">
                                    <Typography variant="caption" color="textSecondary">
                                        Stroom normaal
                                    </Typography>
                                    <OutlinedInput
                                        type="text"
                                        id="outlined-adornment-weight"
                                        placeholder="0000"
                                        endAdornment={<InputAdornment position="end">kWh</InputAdornment>}
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <span className="icon-as-stroomlicht icon-size" />
                                            </InputAdornment>
                                        }
                                        aria-describedby="outlined-weight-helper-text"
                                        className='verbruik-input'
                                        inputProps={{
                                            'aria-label': 'weight',
                                            inputMode: "numeric"
                                        }}
                                        value={estimatedVerbruik.e_single || ''}
                                        onChange={(e) => handleChange('e_single', e.target.value)}
                                        onBlur={(e) => handleOnBlur('e_single', e.target.value)}
                                        error={eSingleInputError}
                                        helperText={eSingleInputError && 'Gelieve je stroomverbruik in te vullen. Verbruik je geen stroom? Vul dan 0 in.'}
                                    />
                                    {eSingleInputError && (
                                        <FormHelperText error={eSingleInputError}>
                                            Gelieve je normaal stroomverbruik in te vullen. Verbruik je geen stroom? Vul dan 0 in.
                                        </FormHelperText>
                                    )}
                                </FormControl>

                            ) : (
                                <>
                                    <div class="verbruik-stroom-hoog">
                                        <FormControl variant="outlined">
                                            <Typography variant="caption" color="textSecondary">
                                                Stroom hoog
                                            </Typography>
                                            <OutlinedInput
                                                type="text"
                                                id="outlined-adornment-weight"
                                                placeholder="0000"
                                                endAdornment={<InputAdornment position="end">kWh</InputAdornment>}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <span className="icon-as-stroomlicht icon-size" />
                                                    </InputAdornment>
                                                }

                                                aria-describedby="outlined-weight-helper-text"
                                                className='verbruik-input'
                                                inputProps={{
                                                    'aria-label': 'weight',
                                                    inputMode: "numeric"
                                                }}
                                                value={estimatedVerbruik && estimatedVerbruik.e_high ? estimatedVerbruik.e_high : ''}
                                                onChange={(e) => handleChange('e_high', e.target.value)}
                                                onBlur={(e) => handleOnBlur('e_high', e.target.value)}
                                                error={eHighInputError}
                                                helperText={eHighInputError && 'Gelieve je hoog stroomverbruik in te vullen. Verbuis je geen gas? Vul dan 0 in.'}
                                            />

                                            {eHighInputError && (
                                                <FormHelperText error={eHighInputError}>
                                                    Gelieve je hoog stroomverbruik in te vullen. Verbruik je geen hoog stroom? Vul dan 0 in.
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    </div>
                                    <div class="verbruik-stroom-laag">
                                        <FormControl variant="outlined">
                                            <Typography variant="caption" color="textSecondary">
                                                Stroom laag
                                            </Typography>
                                            <OutlinedInput
                                                type="text"
                                                id="outlined-adornment-weight"
                                                placeholder="0000"
                                                endAdornment={<InputAdornment position="end">kWh</InputAdornment>}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <span className="icon-as-stroomlicht icon-size" />
                                                    </InputAdornment>
                                                }
                                                aria-describedby="outlined-weight-helper-text"
                                                className='verbruik-input'
                                                inputProps={{
                                                    'aria-label': 'weight',
                                                    inputMode: "numeric"
                                                }}
                                                value={estimatedVerbruik && estimatedVerbruik.e_low ? estimatedVerbruik.e_low : ''}
                                                onChange={(e) => handleChange('e_low', e.target.value)}
                                                onBlur={(e) => handleOnBlur('e_low', e.target.value)}
                                                error={eLowInputError}
                                                helperText={eLowInputError && 'Gelieve je laag stroomverbruik in te vullen. Verbuis je geen gas? Vul dan 0 in.'}
                                            />
                                            {eLowInputError && (
                                                <FormHelperText error={eLowInputError}>
                                                    Gelieve je laag stroomverbruik in te vullen. Verbruik je geen laag stroom? Vul dan 0 in.
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <div class="verbruik-gas">
                                <FormControl variant="outlined">
                                    <Typography variant="caption" color="textSecondary">
                                        Gas
                                    </Typography>
                                    <OutlinedInput
                                        type="text"
                                        id="outlined-adornment-weight"
                                        endAdornment={<InputAdornment position="end">M³</InputAdornment>}
                                        placeholder="0000"
                                        startAdornment={
                                            <InputAdornment position="start">
                                                <span className="icon-as-gaslicht icon-size" />
                                            </InputAdornment>
                                        }
                                        aria-describedby="outlined-weight-helper-text"
                                        className='verbruik-input'
                                        inputProps={{
                                            'aria-label': 'weight',
                                            inputMode: "numeric"
                                        }}
                                        value={estimatedVerbruik && estimatedVerbruik.gas ? estimatedVerbruik.gas : ''}
                                        onChange={(e) => handleChange('gas', e.target.value)}
                                        onBlur={(e) => handleOnBlur('gas', e.target.value)}
                                        error={gasInputError}
                                        helperText={gasInputError && 'Gelieve je gasverbruik in te vullen. Verbuis je geen gas? Vul dan 0 in.'}
                                    />
                                    {gasInputError && (
                                        <FormHelperText error={gasInputError}>
                                            Gelieve je gasverbruik in te vullen. Verbruik je geen gas? Vul dan 0 in.
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </div>
                        </>
                    )}
                </div>

            ))}
            <>
                {shouldShowZelfopwek(chosenProduct, returnsAvailableForAgent, true) && (
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={returnsEnergy}
                                    onChange={() => {
                                        const newReturnsEnergyValue = !returnsEnergy;
                                        setReturnsEnergy(newReturnsEnergyValue);
                                    }}
                                    name="returnsEnergySwitch"
                                    color="primary"
                                />
                            }
                            label={dubbeleMeter ? 'Ik wek zelf stroom op' : 'Ik wek zelf stroom op'} 
                        />
                    </FormGroup>
                )}
            </>
            {returnsEnergy && (
                <TerugleveringInput
                    returnedEnergyAmount={returnedEnergyAmount}
                    setReturnedEnergyAmount={setReturnedEnergyAmount}
                    dubbeleMeter={dubbeleMeter}
                />
            )}
            <div className="help-mij-met-inschatten">
                <a onClick={handleModalOpen} style={{ cursor: 'pointer', color: '#007681' }}>
                    help mij met inschatten
                </a>
            </div>

            <VerbruikInschatter
                modalOpen={modalOpen}
                handleModalClose={handleModalClose}
                dubbeleMeter={dubbeleMeter}
                teruglevering={returnsEnergy}
                entiteit={entiteit}
                selectedBuildingType={selectedBuildingType}
                setSelectedBuildingType={setSelectedBuildingType}
                householdSize={householdSize}
                sethouseholdSize={sethouseholdSize}
                setReturnedEnergyAmount={setReturnedEnergyAmount}
                returnedEnergyAmount={returnedEnergyAmount}
                solarPanels={solarPanels}
                setSolarPanels={setSolarPanels}
                setIsFetchingData={setIsFetchingData}
                setVerbruikEstimaterStep2Output={setVerbruikEstimaterStep2Output}
                verbruikEstimaterStep2Output={verbruikEstimaterStep2Output}
                setFinalChosenUsage={setFinalChosenUsage}
                agentID={agentID}
            />

        </div>


    );
}
VerbruikInput.displayName = 'VerbruikInput';
export default VerbruikInput;
